import React from 'react';

// Components
import ShelfStatus from '@/views/components/shelf-management/ShelfStatus';
import { Button } from '@/views/components/buttons';
import { LoadingSpinner } from '@/views/components/common';
import { PillLabel } from '@/views/components/misc';
import { Tooltip } from '@/views/components/tooltips';

// Enums
import { INSTRUMENT_TYPE } from '@/enums/InstrumentByCode';

// Libraries
import classNames from 'classnames';

// Types
import Nullable from '@/types/nullable';
import PortfolioHoldingType from '@/types/portfolio-holding-type';
import ProxySimulationOptionsType from '@/types/proxy-simulation-options-type';

// Utils
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { formatMessage } from '@/utils/i18n';
import useScreenSize from '@/hooks/useScreenSize';

const i18nBasePath = 'Search.QuickSearchV2';

export type QUICK_SEARCH_V2_ITEM = {
  lineItemAddLabel?: string;
  code?: Nullable<string>;
  flags?: string[];
  id: string;
  isFavorite?: boolean;
  isProxy?: boolean;
  name: string;
  pillLabel?: INSTRUMENT_TYPE;
  type?: COMPARISON_ITEM_TYPES | PortfolioHoldingType;
  proxyType?: string;
  options?: ProxySimulationOptionsType;
  shelfName?: string;
};

type Props = {
  isAddedItem?: boolean;
  isAddingItem?: boolean;
  isSingleAdd?: boolean;
  item: QUICK_SEARCH_V2_ITEM;
  onAddItem: (item: QUICK_SEARCH_V2_ITEM) => void;
};

const QuickSearchV2Item = ({ isAddedItem, isAddingItem, isSingleAdd, item, onAddItem }: Props) => {
  const { isDesktop } = useScreenSize();
  const NAME_MAX_LIMIT = isDesktop ? 40 : 25;
  const addItemDefaultLabel = formatMessage('Search.QuickSearchV2.AddItem');

  const { id, lineItemAddLabel, code, flags, isFavorite, name, pillLabel } = item;
  const isDisabled = !id || isAddingItem || (isAddedItem && isSingleAdd);

  const renderItemAddLabel = () => {
    if (isAddingItem) {
      return <LoadingSpinner />;
    }
    const itemLabel = !isAddedItem ? (lineItemAddLabel ?? addItemDefaultLabel) : formatMessage('Search.QuickSearchV2.Added');
    return <span className="add-label">{itemLabel}</span>;
  };

  const renderCode = () => {
    if (code === null) {
      return null;
    }
    return <p className={classNames('symbol', { 'empty-value': !code })}>{code}</p>;
  };

  const renderItemName = () => {
    if (name.length > NAME_MAX_LIMIT) {
      return (
        <Tooltip className="custom-tooltip-v2" message={name} place="bottom">
          <p>{name}</p>
        </Tooltip>
      );
    }
    return <p>{name}</p>;
  };

  const renderPillLabel = () => (
    <span className={classNames('pill', { 'empty-value': !pillLabel })}>
      {pillLabel && (
        <PillLabel className={pillLabel} text={formatMessage(`${i18nBasePath}.Item.PillLabels.${pillLabel.toUpperCase()}`, undefined, pillLabel)} />
      )}
    </span>
  );

  const renderSavedIcon = () => <span className="favorite">{isFavorite && <i className="icon-star-full" />}</span>;

  const renderShelfStatus = () => flags && <ShelfStatus isV2 statuses={flags} tooltipClassName="custom-tooltip-v2" title={item.shelfName} />;

  return (
    <Button
      className={classNames('quick-search-v2-item', { 'is-added-item': isAddedItem })}
      data-testid={`quickSearchV2-resultItem-${id}`}
      disabled={isDisabled}
      onClick={() => onAddItem(item)}
      role="option"
      // Force safari to give button focus for blur event to work properly
      tabIndex={0}
    >
      {renderSavedIcon()}
      {renderPillLabel()}
      {renderCode()}
      {renderItemName()}
      {renderShelfStatus()}
      {renderItemAddLabel()}
    </Button>
  );
};

export default QuickSearchV2Item;
